import axios from 'axios';
import {
  BillingInformationModel,
  CheckoutModel,
  CustomerInformationModel,
  HubSpotCompany,
  HubSpotContact,
  LicenseNotificationModel,
  LicenseWarningModel,
  PendingOrder,
  Product,
  ProductAssociationMap,
  StripeAchPaymentSourceModel,
  StripeCreditCardPaymentSourceModel,
  StripeKeyRecord,
  StripePaymentSourceModel,
  SubscriptionsRecord,
} from './billing-types';
import { queryOptions } from '@tanstack/react-query';
import { Messages } from '../utils/message-constants';
import { auth } from '../shared/firebase/firebase-config';

const BASE_URL =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8888'
    : window.location.origin;

const instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

instance.interceptors.request.use(async (config) => {
  const idToken = await auth.currentUser?.getIdToken();
  if (idToken) {
    config.headers.Authorization = `Bearer ${idToken}`;
  }
  return config;
});

export const BillingKeys = {
  ProductAssociations: 'productAssociations',
  CustomerBillingInformation: 'customerBillingInformation',
  Subscriptions: 'subscriptions',
  PendingOrders: 'pendingOrders',
  HubSpotCompany: 'HubSpotCompany',
  HubSpotContacts: 'HubSpotContacts',
  LicenseNotificationModel: 'LicenseNotificationModel',
  StripeKeyType: 'StripeKeyType',
};

export async function getProductAssociations() {
  const res = await instance.get<ProductAssociationMap>(
    `/api/billing/getProductAssociations`
  );
  return res.data;
}

export async function getSubscriptions(
  productAssociationMap: ProductAssociationMap
) {
  const res = await instance.post<SubscriptionsRecord>(
    `/api/billing/getSubscriptionsWithMetadata`,
    productAssociationMap
  );
  return res.data;
}

export async function getCustomerBillingInformation() {
  const res = await instance.get<BillingInformationModel>(
    `/api/billing/getCustomerBillingInformation`
  );
  return res.data;
}

export async function getPendingOrdersForType(type: string) {
  const res = await instance.get<PendingOrder[]>(
    `/api/billing/getPendingOrdersForType`,
    {
      params: { type },
    }
  );
  return res.data;
}

export async function getHubSpotCompany() {
  const res = await instance.get<HubSpotCompany>(
    `/api/billing/getHubSpotCompany`
  );
  return res.data;
}

export async function getHubSpotContactsByCompanyId(companyId: number) {
  const res = await instance.get<HubSpotContact[]>(
    `/api/billing/getHubSpotContactsByCompanyId`,
    {
      params: { companyId },
    }
  );
  return res.data;
}

export async function deleteHubSpotContact(
  contact: HubSpotContact,
  changeLogMessage: string
) {
  const res = await instance.delete<void>(`/api/billing/removeHubSpotContact`, {
    data: contact,
    params: { changeLogMessage },
  });
  return res.data;
}

export async function getLicenseNotificationModel(product: Product) {
  const res = await instance.get<LicenseNotificationModel>(
    `/api/billing/getLicenseNotificationModel`,
    {
      params: { product },
    }
  );
  return res.data;
}

export async function updateLicenseNotificationModel(
  model: LicenseNotificationModel
) {
  const res = await instance.put<void>(
    `/api/billing/updateLicenseNotificationModel`,
    model
  );
  return res.data;
}

export async function createHubspotContact(
  newContact: Partial<HubSpotContact>,
  companyId: number,
  changeLogMessage: string
) {
  const res = await instance.post<HubSpotContact[]>(
    `/api/billing/createHubSpotContact`,
    newContact,
    {
      params: {
        companyId,
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function updateHubSpotContact(
  contact: Partial<HubSpotContact>,
  changeLogMessage: string
) {
  const res = await instance.put<HubSpotContact>(
    `/api/billing/updateHubSpotContact`,
    contact,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function deletePaymentMethod(
  qb_instance: 'us' | 'canada',
  model: StripePaymentSourceModel,
  changeLogMessage: string
) {
  const res = await instance.delete<void>(
    `/api/billing/removeStripePaymentMethod`,
    {
      data: model,
      params: {
        qb_instance,
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function getStripeKeyDetails(qbInstance: 'us' | 'canada') {
  const res = await instance.get<StripeKeyRecord>(
    `/api/billing/getStripeKeyDetails`,
    {
      params: { qbInstance },
    }
  );

  return res.data;
}

export async function attachCardToCustomerInStripe(
  stripeCustomerId: string,
  sourceToken: string,
  nickName: string,
  billingInformationModel: BillingInformationModel,
  changeLogMessage: string
) {
  const res = await instance.post<StripePaymentSourceModel>(
    `/api/billing/attachCardToCustomerInStripe`,
    billingInformationModel,
    {
      params: {
        stripeCustomerId,
        sourceToken,
        nickName,
        changeLogMessage,
      },
    }
  );

  return res.data;
}

export async function attachAchToCustomerInStripe(
  paymentSourceModel: Partial<StripeAchPaymentSourceModel>,
  billingInformationModel: BillingInformationModel,
  changeLogMessage: string
) {
  const res = await instance.post<StripePaymentSourceModel>(
    `/api/billing/attachAchToCustomerInStripe`,
    {
      paymentSourceModel,
      billingInformationModel,
      changeLogMessage,
    }
  );
  return res.data;
}

export async function updateCustomerAutoPayStatus(autoPayEnabled: boolean) {
  const res = await instance.put<CustomerInformationModel>(
    `/api/billing/updateCustomerAutoPayStatus`,
    autoPayEnabled
  );
  return res.data;
}

export async function updateStripeSourceToAutoPay(
  stripePaymentSource: StripePaymentSourceModel,
  isAutoPay: boolean
) {
  const res = await instance.post<StripePaymentSourceModel>(
    `/api/billing/updateStripeSourceToAutoPay`,
    stripePaymentSource,
    { params: { isAutoPay } }
  );
  return res.data;
}

export async function checkout(
  checkoutModel: CheckoutModel,
  newCreditCard: boolean,
  changeLogMessage: string
) {
  const res = await instance.post<string>(
    `/api/billing/checkout?newCreditCard=${newCreditCard}`,
    checkoutModel,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function saveLicenseCountWarningMessageForBulk(
  productName: string,
  currentLicenseCount: number,
  numOfAdditionalLicense: number
) {
  const res = await instance.post<LicenseWarningModel>(
    `/api/billing/saveLicenseCountWarningMessageForBulk`,
    productName,
    { params: { currentLicenseCount, numOfAdditionalLicense } }
  );
  return res.data;
}

export async function updateAutoPay(
  autoPayEnabled: boolean,
  oldStripePaymentSourceModel: StripePaymentSourceModel | undefined,
  newStripePaymentSourceModel: StripePaymentSourceModel | undefined,
  changeLogMessage: string
) {
  const res = await instance.put<void>(`/api/billing/updateAutoPay`, {
    oldStripePaymentSourceModel,
    newStripePaymentSourceModel,
    autoPayEnabled,
    changeLogMessage,
  });

  return res.data;
}

export async function updateCreditCardInfo(
  billingInformationModel: BillingInformationModel,
  stripePaymentSourceModel: StripeCreditCardPaymentSourceModel,
  changeLogMessage: string
) {
  const res = await instance.put<StripePaymentSourceModel>(
    `/api/billing/updateCreditCardInfo`,
    {
      billingInformationModel,
      stripePaymentSourceModel,
    },
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function updateAchInfo(
  billingInformationModel: BillingInformationModel,
  stripePaymentSourceModel: StripeAchPaymentSourceModel,
  changeLogMessage: string
) {
  const res = await instance.put<StripePaymentSourceModel>(
    `/api/billing/updateAchInfo`,
    {
      billingInformationModel,
      stripePaymentSourceModel,
    },
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function verifyAchDeposits(
  billingInformationModel: BillingInformationModel,
  sourceModel: StripeAchPaymentSourceModel,
  depositOne: string,
  depositTwo: string
) {
  const res = await instance.post<void>(`/api/billing/verifyAchDeposits`, {
    billingInformationModel,
    sourceModel,
    depositOne,
    depositTwo,
  });

  return res.data;
}

export async function setContactToBillingContact(
  contactEmail: string,
  companyId: number
) {
  const res = await instance.put<HubSpotContact>(
    `/api/billing/setBillingContact`,
    null,
    {
      params: {
        contactEmail,
        companyId,
      },
    }
  );
  return res.data;
}

export async function validateBillingCustomerAddresses(
  billingInformationModel: BillingInformationModel
) {
  const res = await instance.post<void>(
    `/api/billing/validateBillingCustomerAddresses`,
    billingInformationModel
  );

  return res.data;
}

export async function getHubspotContactByEmail(email: string) {
  const res = await instance.get<HubSpotContact>(
    `/api/billing/getHubspotContactByEmail`,
    {
      params: {
        email,
      },
    }
  );
  return res.data;
}

export function getBillingInfoModelQuery() {
  return queryOptions({
    queryKey: [BillingKeys.CustomerBillingInformation],
    queryFn: () => getCustomerBillingInformation(),
    staleTime: 0,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('customer information'),
    },
  });
}

export function getHubSpotCompanyQuery() {
  return queryOptions({
    queryKey: [BillingKeys.HubSpotCompany],
    queryFn: () => getHubSpotCompany(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('company data'),
    },
  });
}

export async function uploadPurchaseOrderToDrive(
  purchaseOrderNumber: string,
  purchaseOrderUpload: File
) {
  const formData = new FormData();
  formData.append('purchaseOrderNumber', purchaseOrderNumber);
  formData.append('file', purchaseOrderUpload);

  const res = await instance.post<string>(
    `/api/billing/uploadPurchaseOrderToDrive`,
    formData
  );
  return res.data;
}

export async function setInitialPrimaryBillingContact(
  hubSpotContact: Partial<HubSpotContact>,
  companyId: number,
  qbInstance: 'us' | 'canada'
) {
  const res = await instance.post<void>(
    `/api/billing/setInitialPrimaryBillingContact`,
    hubSpotContact,
    {
      params: {
        companyId,
        qbInstance,
      },
    }
  );
  return res.data;
}
